import {getAssetId} from "../../asset/util/assetUtils";
import {SWAP_SLIPPAGE} from "../../core/constants";
import {AsyncProcessState} from "../../core/network/async-process/asyncProcessTypes";
import useAsyncProcess from "../../core/network/async-process/useAsyncProcess";
import {useSwapFlowDispatch} from "../../swap/flow/context/SwapFlowContext";
import {SwapFlowView} from "../../swap/flow/context/SwapFlowContext.types";
import {
  determineSwapAmounts,
  generateSwapTxns,
  getSwapFormErrorMessage,
  getSwapFundSufficiencyInformation,
  getSwapTypeFromFormState
} from "../../swap/form/SwapForm.utils";
import {
  useSwapFormDispatch,
  useSwapFormState
} from "../../swap/form/context/SwapFormContext";
import {
  useSelectedAccount,
  useSelectedAccountAddress,
  useSelectedAccountAssets
} from "../../wallet/util/context/WalletContext.utils";
import {ButtonProps} from "../button/Button.types";
import ContainerButton from "../button/container/ContainerButton";
import TooltipContent from "../tooltip/content/TooltipContent";

interface SwapButtonProps {
  asyncProcessState: AsyncProcessState;
  customClassName?: string;
}

function SwapButton({customClassName, asyncProcessState}: SwapButtonProps) {
  const state = useSwapFormState();
  const dispatch = useSwapFormDispatch();
  const dispatchSwapFlowAction = useSwapFlowDispatch();
  const selectedAccount = useSelectedAccount();
  const selectedAccountAddress = useSelectedAccountAddress();
  const selectedAccountAssets = useSelectedAccountAssets();
  const {assetInAmount, assetOutAmount} = determineSwapAmounts(state);
  const hasEnteredAmount = Boolean(assetInAmount) || Boolean(assetOutAmount);
  const {error, isRequestPending} = asyncProcessState;
  const fundSufficiencyInfo = getSwapFundSufficiencyInformation({
    account: selectedAccount,
    accountAssets: selectedAccountAssets,
    swapFormState: state
  });
  const {runAsyncProcess: runGenerateSwapTxnsProcess, state: generateSwapTxnsAsyncState} =
    useAsyncProcess();

  return (
    <ContainerButton
      customClassName={customClassName}
      tooltipProps={getSwapButtonTooltipProps()}
      type={"button"}
      onClick={handleClick}
      shouldDisplaySpinner={generateSwapTxnsAsyncState.isRequestPending}
      isDisabled={
        !hasEnteredAmount ||
        isRequestPending ||
        Boolean(error?.message) ||
        !fundSufficiencyInfo.algoBalance.hasEnough ||
        !fundSufficiencyInfo.assetInBalance.hasEnough
      }>
      {"Swap"}
    </ContainerButton>
  );

  async function handleClick() {
    if (selectedAccountAddress) {
      try {
        const txnsToSign = await runGenerateSwapTxnsProcess(
          generateSwapTxns({
            accountAddress: selectedAccountAddress,
            assetOutID: getAssetId(state.assetOut.asset!),
            quote: state.quote!,
            slippage: SWAP_SLIPPAGE,
            swapType: getSwapTypeFromFormState(state),
            accountAssets: selectedAccountAssets
          })
        );

        dispatch({
          type: "SET_TRANSACTIONS_TO_SIGN",
          txnsToSign
        });

        dispatchSwapFlowAction({
          type: "SET_VIEW",
          view: SwapFlowView.Sign
        });
      } catch {
        //  TODO: Handle error properly
        console.error("error");
      }
    }
  }

  function getSwapButtonTooltipProps(): ButtonProps["tooltipProps"] {
    const errorMessage = getSwapFormErrorMessage(fundSufficiencyInfo, error);

    if (errorMessage) {
      return {
        content: (
          <TooltipContent customClassName={"swap-form__swap-button__tooltip-content"}>
            {errorMessage}
          </TooltipContent>
        ),
        placement: "top",
        controlledTooltip: {
          open: true
        }
      };
    }

    return undefined;
  }
}

export default SwapButton;
